import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Clock, CheckCircle, Sparkles, RefreshCw, Download } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  converted: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
};

const SIPLeadsReport = () => {
  const { token } = useAdmin();
  const [leads, setLeads] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [leadsRes, reportRes] = await Promise.all([
        axios.get(`${API}/admin/sip-leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/reports/sip-leads`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLeads(leadsRes.data);
      setReport(reportRes.data);
    } catch {
      toast.error('Failed to fetch SIP leads');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [fetchData, token]);

  const updateStatus = async (leadId, status) => {
    try {
      await axios.patch(
        `${API}/admin/sip-leads/${leadId}?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const res = await fetch(`${API}/admin/sip-leads/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sip_leads_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sip-leads-report">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">DNO SIP Plan — Safar Samridhi</h1>
          <p className="text-slate-500 text-sm">Manage Safar Samridhi enrollment leads</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportCSV} variant="outline" size="sm" disabled={exporting || leads.length === 0} data-testid="sip-export-csv">
            <Download className="h-4 w-4 mr-1" /> {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      {report && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3 flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{report.total_leads}</p>
                <p className="text-xs text-slate-500">Total Leads</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg"><Sparkles className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{report.new}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg"><Clock className="h-5 w-5 text-yellow-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{report.contacted}</p>
                <p className="text-xs text-slate-500">Contacted</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{report.converted}</p>
                <p className="text-xs text-slate-500">Converted</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan breakdown */}
      {report?.by_plan?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">By Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {report.by_plan.map((p) => (
                <Badge key={`${p.plan_type}-${p.plan_tier}`} variant="outline" className="px-3 py-1.5 capitalize">
                  {p.plan_type} — {p.plan_tier}: <strong className="ml-1">{p.total}</strong>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-slate-400">No SIP leads yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">{lead.name}</TableCell>
                    <TableCell>
                      <div className="text-sm">{lead.email}</div>
                      <div className="text-xs text-slate-500">{lead.mobile}</div>
                    </TableCell>
                    <TableCell>
                      <span className="capitalize text-sm">{lead.plan_type} — {lead.plan_tier}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[lead.status] || 'bg-slate-100 text-slate-800'}>
                        {lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {lead.created_at ? format(new Date(lead.created_at), 'dd MMM yyyy') : '—'}
                    </TableCell>
                    <TableCell>
                      <Select onValueChange={(val) => updateStatus(lead.id, val)} defaultValue={lead.status}>
                        <SelectTrigger className="w-28 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SIPLeadsReport;
